/**
 * Glyph set, drawn in the SF Symbols idiom: 24-unit grid, rounded caps and
 * joins, optical weight matched to 13–15px text.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Glyph({ children, ...rest }: IconProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const CalendarIcon = (p: IconProps) => (
  <Glyph {...p}>
    <rect x="3" y="5" width="18" height="16" rx="4" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Glyph>
);

export const PlusIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M12 5v14M5 12h14" /></Glyph>
);

export const MinusIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M5 12h14" /></Glyph>
);

export const PlaneIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M10.5 3.5a1.5 1.5 0 0 1 3 0V9l7.5 4.2v2.3l-7.5-2.3v4l2.5 1.8v1.6L12 19.8l-4 .8v-1.6l2.5-1.8v-4L3 15.5v-2.3L10.5 9z" />
  </Glyph>
);

export const PeopleIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20a6 6 0 0 1 12 0M16.5 5.6a3.2 3.2 0 0 1 0 6.3M18 14.4a6 6 0 0 1 3 5.6" />
  </Glyph>
);

export const GraduationIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M12 4 2.5 8.6 12 13.2l9.5-4.6z" />
    <path d="M6.5 11v5.2c0 1.6 2.5 2.9 5.5 2.9s5.5-1.3 5.5-2.9V11M21.5 8.6v5.6" />
  </Glyph>
);

export const WalletIcon = (p: IconProps) => (
  <Glyph {...p}>
    <rect x="2.5" y="5.5" width="19" height="13.5" rx="3.5" />
    <path d="M2.5 10h19" />
    <circle cx="17.5" cy="14.5" r="1.1" fill="currentColor" stroke="none" />
  </Glyph>
);

export const WrenchIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M15.5 3.5a5.5 5.5 0 0 0-5 7.7L3.8 17.9a2 2 0 0 0 2.8 2.8l6.7-6.7a5.5 5.5 0 0 0 6.9-7.3l-3 3-2.9-.6-.6-2.9 3-3a5.5 5.5 0 0 0-1.2-.7z" />
  </Glyph>
);

export const PhoneIcon = (p: IconProps) => (
  <Glyph {...p}>
    <rect x="6" y="2.5" width="12" height="19" rx="3.2" />
    <path d="M10.5 18.5h3" />
  </Glyph>
);

export const LockIcon = (p: IconProps) => (
  <Glyph {...p}>
    <rect x="4.5" y="10" width="15" height="11" rx="3.4" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </Glyph>
);

export const BookIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M4 4.5h5.5A2.5 2.5 0 0 1 12 7v13a2 2 0 0 0-2-2H4z" />
    <path d="M20 4.5h-5.5A2.5 2.5 0 0 0 12 7v13a2 2 0 0 1 2-2h6z" />
  </Glyph>
);

export const DocIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M13.5 3H7a2.5 2.5 0 0 0-2.5 2.5v13A2.5 2.5 0 0 0 7 21h10a2.5 2.5 0 0 0 2.5-2.5V9z" />
    <path d="M13.5 3v4.5A1.5 1.5 0 0 0 15 9h4.5" />
  </Glyph>
);

export const BellIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9z" />
    <path d="M13.7 20a2 2 0 0 1-3.4 0" />
  </Glyph>
);

/** A cog: where the school is set up rather than operated. */
export const GearIcon = (p: IconProps) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M19.4 15a1.6 1.6 0 0 0 .32 1.77l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-1 1.47V21a2 2 0 1 1-4 0v-.11a1.6 1.6 0 0 0-1.05-1.47 1.6 1.6 0 0 0-1.77.32l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-1H3a2 2 0 1 1 0-4h.11a1.6 1.6 0 0 0 1.47-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.77.32H9a1.6 1.6 0 0 0 1-1.47V3a2 2 0 1 1 4 0v.11a1.6 1.6 0 0 0 1 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.32 1.77V9a1.6 1.6 0 0 0 1.47 1H21a2 2 0 1 1 0 4h-.11a1.6 1.6 0 0 0-1.47 1z" />
  </Glyph>
);

/** A pencil on a page: start writing to somebody new. */
export const ComposeIcon = (p: IconProps) => (
  <Glyph {...p}>
    <path d="M20 13.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.5" />
    <path d="M18.4 3.6a1.9 1.9 0 0 1 2.7 2.7L13.9 13.5 10 14.5l1-3.9z" />
  </Glyph>
);

/** An envelope. What the inbox button wears, because that is what it opens. */
export const MailIcon = (p: IconProps) => (
  <Glyph {...p}>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M3 7.5l8.1 5.4a1.6 1.6 0 0 0 1.8 0L21 7.5" />
  </Glyph>
);

export const ChevronRight = (p: IconProps) => (
  <Glyph strokeWidth={2.4} {...p}><path d="M9 5l7 7-7 7" /></Glyph>
);

export const ChevronLeft = (p: IconProps) => (
  <Glyph strokeWidth={2.4} {...p}><path d="M15 5l-7 7 7 7" /></Glyph>
);

export const ChevronDown = (p: IconProps) => (
  <Glyph strokeWidth={2.4} {...p}><path d="M5 9l7 7 7-7" /></Glyph>
);

export const CheckIcon = (p: IconProps) => (
  <Glyph strokeWidth={2.4} {...p}><path d="M4.5 12.5l5 5 10-11" /></Glyph>
);

export const CheckCircleIcon = (p: IconProps) => (
  <Glyph {...p}><circle cx="12" cy="12" r="9" /><path d="M8 12.3l2.7 2.7L16 9.5" strokeWidth={2.2} /></Glyph>
);

export const AlertIcon = (p: IconProps) => (
  <Glyph {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.2" strokeWidth={2.2} /><circle cx="12" cy="16.4" r="1.05" fill="currentColor" stroke="none" /></Glyph>
);

export const InfoIcon = (p: IconProps) => (
  <Glyph {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v5.5" strokeWidth={2.2} /><circle cx="12" cy="7.8" r="1.05" fill="currentColor" stroke="none" /></Glyph>
);

export const CloseIcon = (p: IconProps) => (
  <Glyph strokeWidth={2.2} {...p}><path d="M6 6l12 12M18 6L6 18" /></Glyph>
);

export const SearchIcon = (p: IconProps) => (
  <Glyph {...p}><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" strokeWidth={2.2} /></Glyph>
);

export const ClockIcon = (p: IconProps) => (
  <Glyph {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5.3l3.4 2" /></Glyph>
);

export const DownloadIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M12 3.5v11M7.5 10.5L12 15l4.5-4.5" /><path d="M4.5 17v1.5A2.5 2.5 0 0 0 7 21h10a2.5 2.5 0 0 0 2.5-2.5V17" /></Glyph>
);

export const ShareIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M12 3.5v12M8 7l4-3.5L16 7" /><path d="M5 13v5.5A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V13" /></Glyph>
);

export const MoonIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" /></Glyph>
);

export const SunIcon = (p: IconProps) => (
  <Glyph {...p}><circle cx="12" cy="12" r="4.2" /><path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4" /></Glyph>
);

export const SignOutIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M15 4.5h2.5A2.5 2.5 0 0 1 20 7v10a2.5 2.5 0 0 1-2.5 2.5H15" /><path d="M11 8l-4 4 4 4M7 12h9" /></Glyph>
);

export const GridIcon = (p: IconProps) => (
  <Glyph {...p}><rect x="3" y="3.5" width="7.5" height="7.5" rx="2.2" /><rect x="13.5" y="3.5" width="7.5" height="7.5" rx="2.2" /><rect x="3" y="13" width="7.5" height="7.5" rx="2.2" /><rect x="13.5" y="13" width="7.5" height="7.5" rx="2.2" /></Glyph>
);

export const ListIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M8.5 6.5h12M8.5 12h12M8.5 17.5h12" /><circle cx="4" cy="6.5" r="1.2" fill="currentColor" stroke="none" /><circle cx="4" cy="12" r="1.2" fill="currentColor" stroke="none" /><circle cx="4" cy="17.5" r="1.2" fill="currentColor" stroke="none" /></Glyph>
);

export const PlayIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M7.5 5.2v13.6l11-6.8z" fill="currentColor" /></Glyph>
);

export const StopIcon = (p: IconProps) => (
  <Glyph {...p}><rect x="6.5" y="6.5" width="11" height="11" rx="2.6" fill="currentColor" /></Glyph>
);

export const ShieldIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M12 3l7.5 3v5.5c0 4.6-3.1 8.3-7.5 9.5-4.4-1.2-7.5-4.9-7.5-9.5V6z" /><path d="M9 12l2.2 2.2L15.5 10" strokeWidth={2} /></Glyph>
);

export const GaugeIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M4 17a8.5 8.5 0 1 1 16 0" /><path d="M12 17l4-5.5" strokeWidth={2.2} /><circle cx="12" cy="17" r="1.4" fill="currentColor" stroke="none" /></Glyph>
);

export const InboxIcon = (p: IconProps) => (
  <Glyph {...p}><path d="M3.5 13.5V7A2.5 2.5 0 0 1 6 4.5h12A2.5 2.5 0 0 1 20.5 7v6.5" /><path d="M3.5 13.5H8l1.5 2.5h5l1.5-2.5h4.5v3.5A2.5 2.5 0 0 1 18 19.5H6a2.5 2.5 0 0 1-2.5-2.5z" /></Glyph>
);

export const MoreIcon = (p: IconProps) => (
  <Glyph {...p}><circle cx="5" cy="12" r="1.7" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.7" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1.7" fill="currentColor" stroke="none" /></Glyph>
);
